<template>
  <div class="app">
    <AppCanvas ref="stage" />

    <AppSidebar
      class="app__sidebar"
      position="left"
    >
      <Logo class="app__sidebar-content" slot="content" />
    </AppSidebar>
    <AppSidebar
      class="app__sidebar u-hidden--until-ipadL"
      position="right"
    >
      <p slot="content" class="app__sidebar-content">
        <span>. </span>
        #TheHistoryOfWebDesign Book
         <a
            class="app__buy"
            href="http://bit.ly/WEBhistory"
            target="_blank"
            rel="noopener"
            @mouseenter.prevent="cursorToScroll()"
            @mouseleave.prevent="cursorToClose()"
          >. Buy Now .
          </a>
      </p>
    </AppSidebar>

    <main class="app__wrapper">
      <div class="app__container">
        <transition
          @enter="enter"
          @leave="leave"
          mode="out-in"
          appear
        >
          <router-view class="app__view" :key="$route.fullPath" />
        </transition>
      </div>
    </main>

    <SoundWave />

    <AppNavigator ref="navigator" />
    <div class="app__year">0000</div>
    <Slider ref="yearsSlider" />

    <AppCursor ref="cursor" />

    <Cookiebar
      class="app__cookie"
      :animation="{ enter: enterCookie, leave: leaveCookie, css: false }"
    >
      <CookieContent
        slot="content"
        text="This website use "
        url="https://thefwa.com/cookie-policy\"
        label="cookie 🍪."
      />
      <CookieButton
        slot="button"
        label="Accept"
      />
    </Cookiebar>
  </div>
</template>

<script>
import { TimelineMax, TweenMax, Power4 } from 'gsap/TweenMax';
import Apollo from '@adoratorio/apollo';
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';

import LoaderClass from '@/scripts/LoaderClass';
import Interface from '@/scripts/Interface';
import scrollbar from '@/scripts/scrollbar';
import loop from '@/scripts/loop';
import animations from '@/scripts/animations';
import Cursor from '@/scripts/Cursor';

import AppCanvas from '@/components/AppCanvas.vue';
import AppCursor from '@/components/AppCursor.vue';
import AppSidebar from '@/components/AppSidebar.vue';
import AppNavigator from '@/components/AppNavigator.vue';
import Cookiebar from '@/components/Cookie/index.vue';
import CookieContent from '@/components/Cookie/CookieContent/index.vue';
import CookieButton from '@/components/Cookie/CookieButton/index.vue';
import Slider from '@/components/Slider.vue';
import Logo from '@/components/Logo.vue';
import SoundWave from '@/components/SoundWave.vue';


export default {
  name: 'app',

  components: {
    AppCanvas,
    AppCursor,
    AppSidebar,
    AppNavigator,
    Cookiebar,
    CookieContent,
    CookieButton,
    Slider,
    Logo,
    SoundWave,
  },

  data() {
    return {
      scrollbar,
      cursor: null,
      navigator: null,
    };
  },

  computed: {
    ...mapState(['scrollIsLocked']),
  },

  watch: {
    scrollIsLocked(newValue) {
      if (newValue) {
        this.scrollbar.ref.pause();
        if (document.body.classList.contains('cursor--close')) document.body.classList.remove('cursor--close');
      } else {
        this.scrollbar.ref.play();
        if (!document.body.classList.contains('cursor--close')) document.body.classList.add('cursor--close');
      }
    },
  },

  created() {
    this.calcVh();

    this.handleResize = debounce(() => this.resize(), 100);
    window.addEventListener('resize', this.handleResize);
  },

  mounted() {
    loop.start();

    if (navigator.userAgent.indexOf('CriOS/') > 0
      && navigator.userAgent.indexOf('AppleWebKit/') > 0
    ) {
      this.preventPullToRefresh(document.body);
    }

    this.loader = new LoaderClass(this.$el);

    this.interface = Interface;
    this.interface.init(
      this.$refs.stage.$el,
      this.$refs.navigator.$el,
      this.$refs.cursor.$el.querySelector('.cursor__container'),
      this.$refs.yearsSlider.$el,
    );

    this.scrollbar.init();
    this.scrollbar.ref.manager.options.emitGlobal = true;
    this.scrollbar.ref.pause();

    window.addEventListener('hermes-wheel', event => this.interface.onScroll(event));
    this.$eventHub.$on('loadingEnd', this.enterAfterLoading);
  },

  methods: {
    enter(el, done) {
      if (scrollbar.ref !== null) scrollbar.ref.scrollTo({ y: 0 }, 0);
      const title = el.querySelector('[data-anim="title"]');
      const buttons = Array.from(el.querySelectorAll('[data-anim="button"]'));
      const subtitle = el.querySelector('[data-anim="subtitle"]');
      const caption = el.querySelector('[data-anim="caption"]');
      const tl = new TimelineMax({
        onComplete: done,
      });

      tl.addLabel('start')
        .set(title, { autoAlpha: 0 }, 'start');

      if (this.scrollIsLocked) {
        tl.add(animations.get('titleIn')(title), 'start');
        if (buttons.length > 0 && subtitle === null && !this.$store.state.loading) tl.add(animations.get('buttonIn')(el), 'start+=0.5');
      } else {
        TweenMax.set(title, { scale: 1.2, transformOrigin: window.innerWidth < 1024 ? '50% 50%' : '0% 0%' });
        tl.add(animations.get('titleInPage')(title), 'start');
        if (buttons.length > 0 && subtitle === null && !this.$store.state.loading) tl.add(animations.get('buttonInPage')(el, 'page'), 'start+=0.3');
      }

      if (subtitle !== null) {
        tl.add(animations.get('titleIn')(subtitle), 'start+=0.15');
        tl.add(animations.get('titleIn')(caption), 'start+=0.3');
        if (buttons.length > 0 && !this.$store.state.loading) tl.add(animations.get('buttonIn')(el), 'start+=0.95');
      }

      if (this.$store.state.loading) {
        tl.from('.app__year', 0.3, { y: -80, scaleY: 1.3, autoAlpha: 0 }, 'start+=0.3')
          .add(animations.get('buttonSetLoader')(el), 'start')
          .add(() => {
            this.$store.commit('SET_STATE_TRANSITION', true);
          }, 'start')
          .add(() => this.loader.start());
      }
    },

    leave(el, done) {
      const title = el.querySelector('[data-anim="title"]');
      const buttons = Array.from(el.querySelectorAll('[data-anim="button"]'));
      const subtitle = el.querySelector('[data-anim="subtitle"]');
      const caption = el.querySelector('[data-anim="caption"]');
      const tl = new TimelineMax({
        onComplete: done,
      });

      tl.addLabel('start');

      if (this.scrollIsLocked) {
        tl.add(animations.get('titleOut')(title), 'start');
      }

      if (buttons.length > 0 && subtitle === null) tl.add(animations.get('buttonOut')(el), 'start+=0.3');

      if (subtitle !== null) {
        tl.add(animations.get('titleOut')(subtitle), 'start+=0.15')
          .add(animations.get('titleOut')(caption), 'start+=0.3');
        if (buttons.length > 0) tl.add(animations.get('buttonOut')(el), 'start+=0.6');
      }
    },

    calcVh() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    },

    resize() {
      this.calcVh();
      this.$eventHub.$emit('windowResize');
    },

    enterAfterLoading() {
      this.interface.afterLoading();

      const tl = new TimelineMax({
        onComplete: () => {
          this.$store.commit('SET_STATE_TRANSITION', false);
        },
      });
      tl.addLabel('start')
        .set('.app__canvas', { autoAlpha: 1 }, 'start')
        .to('.sidebar__container', 1.5, { y: 0, ease: Power4.easeInOut }, 'start')
        .to('.sound-wave', 1.5, { opacity: 1, ease: Power4.easeInOut }, 'start+=0.6')
        .to('.app__sidebar-content', 0.9, { autoAlpha: 1, ease: Power4.easeInOut }, 'start+=0.6')
        .add(animations.get('buttonInLoader')(this.$el), 'start')
        .add(this.enterAnimationSliderYear(), 'start')
        .add(() => {
          let menuAnim = null;

          if (this.$store.state.route.name === 'home') {
            menuAnim = this.interface.navigator.enter('init');
          } else {
            menuAnim = this.interface.navigator.toScroll();
          }

          return menuAnim;
        }, 'start');

      const buttonArrow = Array.from(document.querySelectorAll('.button--dsk .button__arrow'));
      if (buttonArrow !== null) {
        Cursor.ref.addTargets([{
          id: 'apollo-page-cta',
          elements: buttonArrow,
          magnetism: {
            mode: Apollo.MODE.PULL,
            easing: {
              mode: Apollo.EASING.OUT_CUBIC,
              duration: 300,
            },
          },
        }]);
      }
    },

    enterAnimationSliderYear() {
      const tl = new TimelineMax();
      const appYearSlider = document.querySelector('.app__slider-year');
      const itemCurrent = appYearSlider.querySelector('.slider-year__item--initial');
      const itemPrev = appYearSlider.querySelector('.slider-year__item--initial-prev');
      const itemNext = appYearSlider.querySelector('.slider-year__item--initial-next');
      if (itemCurrent !== null) itemCurrent.classList.remove('slider-year__item--initial');

      tl.addLabel('start');

      if (itemPrev !== null) {
        tl.to(itemPrev, 0.6, { autoAlpha: 0.2 }, 'start')
          .add(() => {
            itemPrev.classList.remove('slider-year__item--prev');
          });
      }

      if (itemNext !== null) {
        tl.to(itemNext, 0.6, { autoAlpha: 0.2 }, 'start')
          .add(() => {
            itemNext.classList.remove('slider-year__item--next');
          });
      }

      return tl;
    },

    preventPullToRefresh(element) {
      let prevent = false;
      element.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return false;
        const scrollY = window.pageYOffset || document.body.scrollTop
          || document.documentElement.scrollTop;
        prevent = (scrollY === 0);
        return false;
      });

      element.addEventListener('touchmove', (event) => {
        if (prevent) {
          prevent = false;
          event.preventDefault();
        }
      }, { passive: false });
    },

    cursorToScroll() {
      this.$eventHub.$emit('soundInteraction');
      this.$eventHub.$emit('cursorToZoom');
    },

    cursorToClose() {
      if (!this.$store.state.scrollIsLocked) {
        this.$eventHub.$emit('cursorToClose');
      } else {
        this.$eventHub.$emit('cursorToScroll');
      }
    },

    enterCookie(el, done) {
      TweenMax.fromTo(el, 0.8, {
        autoAlpha: 0,
        y: 100,
      }, {
        autoAlpha: 1,
        y: 0,
        ease: Power4.easeInOut,
        onComplete: done,
      });
    },

    leaveCookie(el, done) {
      TweenMax.fromTo(el, 0.8, {
        autoAlpha: 1,
        y: 0,
      }, {
        autoAlpha: 1,
        y: 100,
        ease: Power4.easeInOut,
        onComplete: done,
      });
    },
  },
};
</script>

<style lang="scss">
@import "./scss/base";

.app {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-image: $color-bg;

  &__sidebar {
    p {
      margin: 0;
      text-align: center;

      transform: scale(-1);
    }

    a,
    span {
      display: inline-block;
      color: $color-secondary;
    }
  }

  &__sidebar-mobile {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    text-align: center;
    font-size: font-size(xs);

    z-index: 25;

    a {
      display: inline-block;
      color: $color-secondary;
    }
  }

  &__burger {
    position: fixed;
    top: 25px;
    right: 25px;

    z-index: 40;

    @include mq(ipadL) {
      position: fixed;
      top: unset;
      bottom: 76px;
      left: 35px;
    }

    &.app__burger--loading.burger {
      pointer-events: none;

      .burger__item:first-child,
      .burger__item:last-child {
        transform: scaleX(0);
      }
    }
  }

  &__year {
    position: absolute;
    top: 8vh;
    left: 15.625vw;
    font-size: font-size(xl);

    @include mq($until: ipadL) {
      @include visually-hidden;
    }
  }

  &__wrapper {
    position: fixed;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__container {
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    height: auto;
    min-height: 100%;

    @include mq($and: $is-touch-device) {
      position: relative;
      height: auto;
    }

    & > div {
      position: relative;

      @include mq(ipadL) {
        padding: 8vh 0 0;
      }

      & > section {
        display: grid;
        width: 100%;
        flex: 0 0 100%;
        grid-template-columns: repeat(15, 1fr);
        grid-template-rows: auto;

        perspective: 800;

        @include mq(ipadL) {
          grid-template-columns: repeat(32, 1fr);
        }
      }
    }
  }

  & .sidebar--right .app__sidebar-content {
    font-size: font-size(xs);

    @include mq(ds) {
      font-size: font-size(s);
    }
  }
}
</style>
